import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatEUR, formatDate, mensajeError } from '../lib/api';
import { normalizaTexto } from '../lib/utils';
import { PageHeader, StatusBadge, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Plus, FileText, Stethoscope, ListChecks, Package, CalendarDays, UserPlus, Check, Sparkles, ChevronsUpDown, Pencil, MessageCircle, AlertCircle, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PdfLangButton from '../components/PdfLangButton';
import PaquetePdfButton from '../components/PaquetePdfButton';
import { EtiquetaHialuronidasaSinImporte } from '../components/AvisoHialuronico';
import BudgetCrmDialog from '../components/BudgetCrmDialog';
import WhatsAppPaqueteButton from '../components/WhatsAppPaqueteButton';
import BotonBorrarPresupuesto from '../components/BotonBorrarPresupuesto';
import NewBudgetDialog from '../components/NewBudgetDialog';
import { PatientCombobox } from '../components/PatientCombobox';
import { error as logError } from '../lib/logger';
import { textoRecargoDelPresupuesto } from '../lib/marcasPaciente';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Caducado = su validez ya pasó, lo diga o no la etiqueta de estado.
 *
 * Esta pantalla sí recibe el estado recalculado (GET /api/budgets caduca al
 * vuelo los «pendiente» y los «sent»), pero no toca a los aceptados: uno
 * aceptado en enero con validez hasta marzo sigue diciendo «Aceptado» hoy.
 * Por eso manda la FECHA, igual que en la ficha del paciente, donde hay una
 * copia de esta misma función: la regla de qué está caducado tiene que ser
 * una sola, y mientras no viva en un sitio común las dos dicen lo mismo,
 * palabra por palabra. El último día válido es el propio `valid_until`.
 */
function presupuestoCaducado(b) {
  if (!b) return false;
  if (b.status === 'expirado') return true;
  const hasta = String(b.valid_until || '').slice(0, 10);
  if (!hasta) return false;
  const d = new Date();
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return hasta < hoy;
}

/* ---------------------------------------------------------------------------
 * BUSCADOR POR PACIENTE (la clínica, 2/10/2026)
 * ---------------------------------------------
 * Con sus palabras: «un buscador que permita localizar presupuestos por nombre
 * de paciente […] independientemente de si se escribe con mayúsculas,
 * minúsculas, con tildes o sin ellas, sin necesidad de escribir el nombre
 * exacto».
 *
 * QUIÉN BUSCA: el SERVIDOR (GET /api/budgets?q=…), sobre la colección ENTERA,
 * como en la sección LOPD. La lista de esta pantalla llega recortada a los
 * LIMITE más recientes: filtrar solo aquí dejaría sin encontrar justo el
 * presupuesto antiguo que alguien viene a buscar. La regla vive en
 * backend/routers/documents_router.py::_filtro_busca_presupuestos (nombre
 * grabado y ficha actual, sin tildes y por trozos en cualquier orden; también
 * el teléfono y el NIF enteros y el número del presupuesto).
 *
 * `coincidePresupuesto` solo ADELANTA trabajo mientras la respuesta viene de
 * camino, sobre lo que ya hay en pantalla y con la normalización de todos los
 * filtros de pantalla (`normalizaTexto`). En cuanto el servidor contesta a lo
 * que está escrito, manda él. Y mientras no ha contestado, la pantalla no dice
 * que algo no exista: dice que está buscando.
 * ------------------------------------------------------------------------- */

/** Tope que se le pide al servidor (el de siempre de GET /api/budgets). */
const LIMITE = 500;

/**
 * ¿Entra este presupuesto en lo que se está buscando? Sin texto, todos.
 * Por trozos en CUALQUIER orden y a medias: «maria gar» encuentra a «María
 * García López», y un apellido suelto basta. El número del presupuesto solo
 * cuenta si lo escrito lleva cifras, igual que en el servidor.
 */
export function coincidePresupuesto(b, texto) {
  const q = (texto || '').trim();
  if (!q) return true;
  const conCifras = /[0-9]/.test(q);
  const blob = normalizaTexto([b?.patient_name, conCifras ? b?.number : ''].filter(Boolean).join(' '));
  return normalizaTexto(q).split(/\s+/).filter(Boolean).every((t) => blob.includes(t));
}

// Las etiquetas de los botones de estado, también para decir en qué estado
// no hay nada cuando la búsqueda sí encuentra presupuestos.
const ESTADOS = [['all', 'Todos'], ['draft', 'Borrador'], ['sent', 'Enviados'], ['accepted', 'Aceptados'], ['rejected', 'Rechazados'], ['expirado', 'Caducados']];

export default function BudgetsPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(!!initialPatient);
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  // Phase 24.D — CRM dialog (estado del presupuesto + pagos parciales)
  const [crmBudget, setCrmBudget] = useState(null);
  // Edición de un presupuesto ya creado (cirugía e importes)
  const [editBudget, setEditBudget] = useState(null);
  // Caducados que alguien ha decidido mandar de todas formas. Dura lo que
  // dura la pantalla abierta: mañana el precio estará un día más vencido.
  const [caducadosDesbloqueados, setCaducadosDesbloqueados] = useState([]);
  const desbloquearEnvioCaducado = (b) => {
    // El clic de más (dirección, 2026-09-15): que mandar un precio vencido sea
    // una decisión y no un descuido. Verlo en PDF sigue siendo gratis.
    const aviso = `El presupuesto ${b.number} caducó el ${formatDate(b.valid_until)}.\n\n`
      + 'Si lo mandas, la paciente lo recibe con la validez vencida bajo el texto '
      + '«Le hacemos llegar su presupuesto». Lo normal es editarlo y renovar la '
      + 'validez antes de enviarlo.\n\n¿Mandarlo igual, caducado?';
    if (!window.confirm(aviso)) return;
    setCaducadosDesbloqueados((ids) => (ids.includes(b.id) ? ids : [...ids, b.id]));
  };
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/budgets');
      setItems(res.data);
    } finally { setLoading(false); }
  }, []);

  // Buscador por paciente (2/10/2026). `search` es lo escrito; `buscado`, el
  // texto al que el SERVIDOR ya ha contestado con `encontrados`.
  const [search, setSearch] = useState('');
  const [encontrados, setEncontrados] = useState([]);
  const [buscado, setBuscado] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [falloBusqueda, setFalloBusqueda] = useState('');
  const cajaBusqueda = useRef(null);
  // La última petición lanzada: si una vieja llega tarde, se tira. Tecleando
  // deprisa, la respuesta de «gar» no puede pisar la de «garcia lopez».
  const peticion = useRef(0);

  const buscar = useCallback(async (texto) => {
    const q = (texto || '').trim();
    const mia = peticion.current + 1;
    peticion.current = mia;
    if (!q) {
      setEncontrados([]);
      setBuscado('');
      setBuscando(false);
      setFalloBusqueda('');
      return;
    }
    setBuscando(true);
    setFalloBusqueda('');
    try {
      const r = await api.get('/api/budgets', { params: { q, limit: LIMITE } });
      if (peticion.current !== mia) return;
      setEncontrados(Array.isArray(r.data) ? r.data : []);
      setBuscado(q);
    } catch (err) {
      if (peticion.current !== mia) return;
      // Sin respuesta no se sabe nada: ni que hay coincidencias ni que no.
      setFalloBusqueda(mensajeError(err, 'No se pudo consultar'));
    } finally {
      if (peticion.current === mia) setBuscando(false);
    }
  }, []);

  // Un cuarto de segundo desde la última tecla antes de preguntar, como en
  // Pacientes y en LOPD. Sin texto no se espera: es volver a la lista.
  useEffect(() => {
    const q = search.trim();
    if (!q) { buscar(''); return undefined; }
    const t = setTimeout(() => buscar(q), 250);
    return () => clearTimeout(t);
  }, [search, buscar]);

  // Tras crear, editar, cambiar el estado, mandar o borrar: se vuelve a pedir
  // la lista y, si hay algo escrito, también la búsqueda.
  const recargar = useCallback(() => {
    fetchItems();
    const q = search.trim();
    if (q) buscar(q);
  }, [fetchItems, buscar, search]);

  const limpiarBusqueda = () => {
    setSearch('');
    cajaBusqueda.current?.focus();
  };

  const fetchPatients = useCallback(async () => {
    try {
      const res = await api.get('/api/patients');
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      logError('[BudgetsPage] No se pudieron pre-cargar pacientes:', e?.message || e);
      setPatients([]);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchPatients();
  }, [fetchItems, fetchPatients]);

  // El backend crea con 'pendiente' y caduca a 'expirado'; el CRM usa
  // draft/sent/accepted/rejected. Se normaliza para filtrar y contar.
  const estado = (b) => ({ pendiente: 'draft', confirmado: 'accepted', cancelado: 'rejected' }[b.status] || b.status || 'draft');
  // Lo que se ve: sin texto, la lista de siempre; con texto, lo que el
  // SERVIDOR ha encontrado entre todos (y mientras contesta, el adelanto de
  // aquí sobre lo ya cargado). El filtro de estado va encima de las dos.
  const q = search.trim();
  const alDia = buscado === q && !buscando && !falloBusqueda;
  const base = !q ? items : alDia ? encontrados : items.filter((b) => coincidePresupuesto(b, q));
  const visible = base.filter(b => filter === 'all' ? true : estado(b) === filter);
  const etiquetaEstado = (ESTADOS.find(([s]) => s === filter) || [])[1] || '';

  // Seguimiento comercial (2026-08-27, dirección): ratio de conversión entre
  // presupuestos enviados y aceptados. Un aceptado cuenta también como
  // enviado (llegó al paciente).
  const nEnviados = items.filter(b => ['sent', 'accepted'].includes(estado(b))).length;
  const nAceptados = items.filter(b => estado(b) === 'accepted').length;
  const ratio = nEnviados > 0 ? Math.round((100 * nAceptados) / nEnviados) : null;

  // eslint-disable-next-line no-unused-vars
  const changeStatus = async (b, status) => {
    await api.put(`/api/budgets/${b.id}`, { status });
    toast.success('Estado actualizado');
    recargar();
  };
  // La papelera es la de la ficha (BotonBorrarPresupuesto, 23/10/2026): la
  // misma regla y el mismo motivo, que manda el servidor. El aviso dice QUÉ se
  // borra: el número y, aquí, que es la lista de toda la clínica, de quién.

  // Re-fetch the patient list whenever the dialog opens so a patient created
  // in a different tab/window is immediately available without page refresh.
  useEffect(() => {
    if (dialogOpen) fetchPatients();
  }, [dialogOpen, fetchPatients]);

  return (
    <div>
      <PageHeader title="Presupuestos" subtitle={`${items.length} presupuestos generados`}
        actions={<Button onClick={() => setDialogOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="budget-new-button"><Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto</Button>} />

      <div className="cjo-card p-3 mb-4 flex flex-wrap items-center gap-1" data-testid="budget-filter">
        {/* Buscar por paciente (la clínica, 2/10/2026). Busca el servidor entre
            TODOS, no solo entre los que caben en la lista; los botones de
            estado filtran encima de lo encontrado. */}
        <div className="relative w-full sm:w-72 sm:mr-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden="true" />
          <Input
            ref={cajaBusqueda}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.preventDefault(); setSearch(''); } }}
            placeholder="Buscar por nombre de paciente…"
            title="Nombre o apellidos, con o sin tildes y en cualquier orden. También teléfono, NIF o número de presupuesto."
            aria-label="Buscar presupuestos por nombre de paciente, teléfono, NIF o número"
            autoComplete="off"
            className="pl-9 pr-9"
            data-testid="budget-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={limpiarBusqueda}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-[#13231E] hover:bg-[rgba(46,110,94,0.08)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="Limpiar búsqueda"
              title="Limpiar búsqueda"
              data-testid="budget-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {ESTADOS.map(([s, label]) => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'ghost'} onClick={() => setFilter(s)} className={filter === s ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}>
            {label}
          </Button>
        ))}
        <div className="flex-1" />
        {/* Ratio de conversión enviados → aceptados (2026-08-27, dirección). */}
        <div className="text-sm px-3 py-1 rounded-md bg-[rgba(46,110,94,0.08)] text-[#13231E]" data-testid="budget-conversion" title="Aceptados sobre el total que llegó al paciente (enviados + aceptados)">
          Conversión: <strong>{ratio === null ? '—' : `${ratio}%`}</strong>
          <span className="text-muted-foreground"> · {nAceptados} aceptados de {nEnviados} enviados</span>
        </div>
        {q && (
          <div className="basis-full text-xs text-muted-foreground pt-1.5 px-1" data-testid="budget-search-count" aria-live="polite">
            {falloBusqueda
              ? `No se pudo buscar entre todos los presupuestos (${falloBusqueda}).${base.length ? ' Abajo solo salen los que ya estaban en pantalla.' : ''}`
              : !alDia
                ? 'Buscando entre todos los presupuestos…'
                : `${base.length} ${base.length === 1 ? 'presupuesto encontrado' : 'presupuestos encontrados'} para «${q}»`
                  + (filter !== 'all' ? ` · ${visible.length} en «${etiquetaEstado}»` : '')
                  + (base.length >= LIMITE ? ` · se muestran los ${LIMITE} más recientes` : '')}
          </div>
        )}
      </div>

      <div className="cjo-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : visible.length === 0 ? (
            /* Con algo escrito, nunca «no existe» sin haber mirado: si el
               servidor no ha contestado, se dice que está buscando; si ha
               fallado, que ha fallado. */
            !q ? <EmptyState icon={FileText} title="Sin presupuestos" description="Crea tu primer presupuesto." />
            : falloBusqueda ? <EmptyState icon={AlertCircle} title="No se pudo buscar" description={`${falloBusqueda}. Esto no quiere decir que no haya presupuestos de «${q}»: vuelve a intentarlo.`} action={<Button variant="outline" onClick={() => buscar(q)} data-testid="budget-search-retry">Reintentar</Button>} />
            : !alDia ? <EmptyState icon={Search} title="Buscando…" description={`Buscando «${q}» entre todos los presupuestos.`} />
            : base.length === 0 ? <EmptyState icon={Search} title="Sin coincidencias" description={`Ningún presupuesto coincide con «${q}». Se ha buscado en todos, por nombre y apellidos del paciente (con o sin tildes), teléfono, NIF y número de presupuesto.`} action={<Button variant="outline" onClick={limpiarBusqueda}>Limpiar búsqueda</Button>} />
            : <EmptyState icon={Search} title={`Ninguno en «${etiquetaEstado}»`} description={`Hay ${base.length} ${base.length === 1 ? 'presupuesto' : 'presupuestos'} para «${q}», pero ninguno en «${etiquetaEstado}».`} action={<Button variant="outline" onClick={() => setFilter('all')}>Ver todos los estados</Button>} />
          )
          : <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr><th className="py-3 px-4 font-semibold text-[#13231E]">Nº</th><th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th><th className="py-3 px-4 font-semibold text-[#13231E]">Fecha</th><th className="py-3 px-4 font-semibold text-[#13231E]">Vence</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Total</th><th className="py-3 px-4 font-semibold text-[#13231E]">Estado</th><th></th></tr>
              </thead>
              <tbody>
                {visible.map(b => {
                  const caducado = presupuestoCaducado(b);
                  const puedeEnviarse = !caducado || caducadosDesbloqueados.includes(b.id);
                  return (
                  <tr key={b.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="budget-row">
                    <td className="py-3 px-4 font-mono text-xs">
                      {b.number}
                      {/* Antes esto iba escrito en las notas del presupuesto y
                          se imprimía en el papel del paciente (2026-07-28).
                          Aquí avisa a quien lo gestiona, no al paciente. */}
                      {b.origen === 'evolutivo' && (
                        <span
                          className="ml-1.5 inline-block rounded px-1.5 py-[1px] text-[9px] font-sans font-bold uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 align-middle"
                          title="Salió del evolutivo automáticamente: revísalo antes de enviarlo"
                          data-testid="budget-origen-evolutivo"
                        >
                          Del evolutivo · revisar
                        </span>
                      )}
                      {/* El recargo de una marca de paciente (14/10/2026): va
                          dentro del precio de la cirugía; aquí, discreto,
                          para quien lo gestiona. */}
                      {textoRecargoDelPresupuesto(b) && (
                        <div className="mt-0.5 text-[10px] font-sans text-muted-foreground" data-testid="budget-recargo">
                          {textoRecargoDelPresupuesto(b)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">{b.patient_name}</td>
                    <td className="py-3 px-4 text-xs">{formatDate(b.date)}</td>
                    {/* La etiqueta de estado no siempre lo canta (un aceptado
                        vencido sigue poniendo «Aceptado»), así que la fecha
                        de vencimiento lo dice ella misma. */}
                    <td className="py-3 px-4 text-xs">
                      {formatDate(b.valid_until)}
                      {caducado && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[10px] font-semibold bg-amber-100 text-amber-900 border-amber-300 align-middle"
                          data-testid="budget-caducado"
                          title={`Su validez venció el ${formatDate(b.valid_until)}`}
                        >
                          <AlertCircle className="w-3 h-3" /> Caducado
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-[#13231E] tabular-nums">{formatEUR(b.total)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge status={b.status} />
                        <EtiquetaHialuronidasaSinImporte budget={b} testId={`budget-hialuronidasa-sin-importe-${b.id}`} />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 items-center justify-end flex-wrap">
                        <PdfLangButton
                          baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/pdf`}
                          filename={`presupuesto_${b.number}.pdf`}
                          title={`Presupuesto ${b.number} · ${b.patient_name}`}
                          onOpen={(p) => setPreview({ open: true, ...p })}
                          dataTestid="budget-pdf-button"
                        >
                          <Button size="sm" variant="outline" title="Ver presupuesto (elige idioma)"><FileText className="w-4 h-4" /></Button>
                        </PdfLangButton>
                        {b.preop_id && (
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/preop/${b.preop_id}/pdf`}
                            filename={`preop_${b.number}.pdf`}
                            title={`Preoperatorio · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-preop-button"
                          >
                            <Button size="sm" variant="outline" title="Ver solicitud de pruebas preoperatorias (elige idioma)"><Stethoscope className="w-4 h-4" /></Button>
                          </PdfLangButton>
                        )}
                        {b.instructions_id && (
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/instructions/${b.instructions_id}/pdf`}
                            filename={`indicaciones_${b.number}.pdf`}
                            title={`Indicaciones · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-instructions-button"
                          >
                            <Button size="sm" variant="outline" title="Ver indicaciones día de cirugía (elige idioma)"><ListChecks className="w-4 h-4" /></Button>
                          </PdfLangButton>
                        )}
                        {(b.preop_id || b.instructions_id) && (
                          /* Con el aviso del ácido hialurónico antes de abrirlo (21/10/2026). */
                          <PaquetePdfButton
                            budget={b}
                            baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/bundle-pdf`}
                            filename={`paquete_${b.number}.pdf`}
                            title={`Paquete completo · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-bundle-button"
                          >
                            <Button size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white" title="Paquete completo (elige idioma)"><Package className="w-4 h-4 mr-1" /> Paquete</Button>
                          </PaquetePdfButton>
                        )}
                        {/* Ver el PDF es gratis; mandar uno vencido cuesta un
                            clic más. Mientras está caducado, en el sitio del
                            botón de WhatsApp hay uno ámbar que lo dice y
                            pregunta (dirección, 2026-09-15). */}
                        {!puedeEnviarse ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                            onClick={() => desbloquearEnvioCaducado(b)}
                            data-testid="budget-caducado-gate"
                            title={`Caducó el ${formatDate(b.valid_until)}: no se manda por descuido. Pulsa para decidirlo.`}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        ) : (
                          <WhatsAppPaqueteButton budget={b} onSent={recargar} dataTestid="budget-whatsapp">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                              title="Paquete online: presupuesto + pruebas preoperatorias por WhatsApp (elige idioma)"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </WhatsAppPaqueteButton>
                        )}
                        {/* Phase 24.D — Botón único de gestión CRM (envío, aceptación, rechazo, pagos) */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCrmBudget(b)}
                          className="border-[#2E6E5E] text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                          data-testid="budget-crm-open"
                          title="Gestionar estado y pagos"
                        >
                          Estado / Pagos
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditBudget(b)}
                          title="Editar cirugía e importes"
                          data-testid="budget-edit-open"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <BotonBorrarPresupuesto budget={b} onBorrado={recargar} conPaciente
                          testId="budget-borrar" />
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      <NewBudgetDialog open={dialogOpen} onOpenChange={setDialogOpen} initialPatient={initialPatient} patients={patients} onPatientsRefresh={fetchPatients} onCreated={recargar} />
      <NewBudgetDialog
        open={!!editBudget}
        onOpenChange={(v) => { if (!v) setEditBudget(null); }}
        budget={editBudget}
        patients={patients}
        onCreated={recargar}
      />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
      <BudgetCrmDialog
        open={!!crmBudget}
        onOpenChange={(v) => { if (!v) setCrmBudget(null); }}
        budget={crmBudget}
        onChanged={recargar}
      />
    </div>
  );
}
